<template>
  <div>
    <!-- Loading skeleton -->
    <div v-if="loading">
      <b-card>
        <b-skeleton width="40%" height="2rem" class="mb-2"/>
        <b-skeleton width="70%" class="mb-1"/>
        <b-skeleton width="55%" class="mb-1"/>
        <b-skeleton-table :rows="5" :columns="4" hide-header/>
      </b-card>
    </div>

    <template v-else>
      <!-- Info Card -->
      <b-card>
        <b-row>
          <b-col md="6">
            <p><strong>Chief Complaint:</strong> {{ consultation.chief_complaint }}</p>
            <p><strong>Vet:</strong> {{ consultation.vet_name || '—' }}</p>
          </b-col>
          <b-col md="6" class="text-right">
            <b-button
              v-if="consultation.status !== 'closed'"
              variant="outline-danger"
              size="sm"
              @click="closeConsultation"
            >
              Close Consultation
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <!-- Chat Area -->
      <b-card class="chat-card">
        <div class="chat-messages" ref="messageContainer" style="max-height: 400px; overflow-y: auto;">
          <div v-if="!messages.length" class="text-center text-muted py-4">
            No messages yet. Start the conversation!
          </div>

          <div
            v-for="msg in messages"
            :key="msg.id"
            class="message-wrapper"
            :class="msg.sender_type === 'farmer' ? 'message-right' : 'message-left'"
          >
            <div class="message-bubble" :class="msg.sender_type === 'farmer' ? 'bubble-farmer' : 'bubble-vet'">
              <small class="sender-name">{{ msg.sender_name || msg.sender_type }}</small>
              <p class="mb-0">{{ msg.message }}</p>
              <small class="text-muted message-time">{{ msg.created_at }}</small>
            </div>
          </div>
        </div>

        <!-- Message Input -->
        <div v-if="consultation.status !== 'closed'" class="chat-input mt-3">
          <b-row>
            <b-col>
              <b-form-input
                v-model="newMessage"
                placeholder="Type your message..."
                @keyup.enter="sendMessage"
              />
            </b-col>
            <b-col cols="auto">
              <b-button variant="primary" :disabled="!newMessage.trim() || sending" @click="sendMessage">
                <b-spinner v-if="sending" small class="mr-1" />
                Send
              </b-button>
            </b-col>
          </b-row>
        </div>
        <div v-else class="text-center text-muted mt-3">
          This consultation is closed.
        </div>
      </b-card>
    </template>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import { BCard, BRow, BCol, BButton, BBadge, BSpinner, BFormInput } from 'bootstrap-vue'

export default {
  name: 'ConsultationDetail',
  components: { BCard, BRow, BCol, BButton, BBadge, BSpinner, BFormInput },
  data() {
    return {
      loading: false,
      sending: false,
      consultation: {},
      messages: [],
      newMessage: '',
      pollInterval: null,
    }
  },
  mounted() {
    this.fetchMessages()
  },
  beforeDestroy() {
    if (this.pollInterval) clearInterval(this.pollInterval)
  },
  methods: {
    async fetchMessages(silent = false) {
      const { consultationId } = this.$route.params
      if (!silent) this.loading = true
      try {
        const { data } = await axios.get(`web/vet-consultations/${consultationId}`)
        this.consultation = data.data || data
        this.messages = (data.data || data).messages || []
        // Start polling once we know status
        if (!silent && this.consultation.status !== 'closed' && !this.pollInterval) {
          this.pollInterval = setInterval(() => {
            this.fetchMessages(true)
          }, 10000)
        }
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        if (!silent) {
          console.error(e)
          this.$bvToast.toast('Failed to load consultation', { variant: 'danger', title: 'Error' })
        }
      } finally {
        if (!silent) this.loading = false
      }
    },
    // Keep backward compat alias
    fetchData() {
      return this.fetchMessages()
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return
      const { consultationId } = this.$route.params
      this.sending = true
      try {
        const { data } = await axios.post(`web/vet-consultations/${consultationId}/messages`, {
          message: this.newMessage,
        })
        // Push the message object (data.data), not the full API wrapper
        this.messages.push(data.data ?? data)
        this.newMessage = ''
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to send message', { variant: 'danger', title: 'Error' })
      } finally {
        this.sending = false
      }
    },
    async closeConsultation() {
      if (!confirm('Close this consultation?')) return
      const { consultationId } = this.$route.params
      try {
        await axios.post(`web/vet-consultations/${consultationId}/close`)
        this.$bvToast.toast('Consultation closed', { variant: 'success', title: 'Success' })
        this.fetchData()
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to close consultation', { variant: 'danger', title: 'Error' })
      }
    },
    scrollToBottom() {
      const el = this.$refs.messageContainer
      if (el) el.scrollTop = el.scrollHeight
    },
    statusVariant(status) {
      const map = { open: 'primary', in_progress: 'warning', closed: 'secondary' }
      return map[status] || 'secondary'
    },
  },
}
</script>

<style scoped>
.chat-card {
  min-height: 60vh;
}
.chat-messages {
  max-height: 60vh;
  overflow-y: auto;
  padding: 10px;
}
.message-wrapper {
  display: flex;
  margin-bottom: 12px;
}
.message-left  { justify-content: flex-start; }
.message-right { justify-content: flex-end; }
.message-bubble {
  max-width: 65%;
  padding: 10px 14px;
  border-radius: 12px;
}
.bubble-farmer {
  background-color: #7367f0;
  color: #fff;
  border-bottom-right-radius: 2px;
}
.bubble-vet {
  background-color: #f0f0f0;
  color: #333;
  border-bottom-left-radius: 2px;
}
.sender-name {
  display: block;
  font-weight: 600;
  margin-bottom: 2px;
  opacity: 0.85;
}
.message-time {
  display: block;
  text-align: right;
  margin-top: 4px;
  font-size: 0.7rem;
}
.bubble-farmer .message-time { color: rgba(255,255,255,0.7); }
</style>
